import CourseStatusNote from "./CourseStatusNote";
import LearningNoteFolders from "./LearningNoteFolders";
import LinkedLearningDescription from "./LinkedLearningDescription";
import ExamCoveragePanel from './ExamCoveragePanel';
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../axiosConfig";
import AiModulesPanel from "./AiModulesPanel";

const EMPTY_LIBRARY_FORM = { title: "", description: "" };
const EMPTY_COURSE_FORM = { title: "", description: "", difficulty: "intermediate" };
const EMPTY_PROGRAM_FORM = {
  title: "",
  certificationLevel: "",
  description: "",
  status: "not_started",
  progressPercent: 0,
  estimatedDurationMinutes: 25,
};
const EMPTY_MODULE_FORM = {
  programId: "",
  title: "",
  description: "",
  status: "not_started",
  progressPercent: 0,
  estimatedDurationMinutes: 25,
};
const EMPTY_LESSON_FORM = {
  title: "",
  summary: "",
  content: "",
  difficulty: "intermediate",
  itemType: "lesson",
  estimatedMinutes: 25,
  domainId: "",
  topicId: "",
};

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatProgress(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0%";
  return `${Math.max(0, Math.min(100, Math.round(number)))}%`;
}

function formatMinutes(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "Not set";
  return `${number} min`;
}

function labelizeStatus(value) {
  return String(value || "not_started").replace(/_/g, " ");
}

function inferTrackIdFromProgram(program, tracks = []) {
  if (!program) return "";
  const haystack = [
    program.title,
    program.certificationLevel,
    program.courseTitle,
  ]
    .map((value) => String(value || "").trim().toUpperCase())
    .filter(Boolean)
    .join(" ");

  const match = (Array.isArray(tracks) ? tracks : []).find((track) => {
    const code = String(track.code || "").trim().toUpperCase();
    const examCode = String(track.examCode || "").trim().toUpperCase();
    const title = String(track.title || "").trim().toUpperCase();
    return [code, examCode, title].filter(Boolean).some((needle) => haystack.includes(needle));
  });

  return match?.id || "";
}

function buildPageMeta(pageType) {
  switch (pageType) {
    case "library":
      return {
        title: "Courses",
        subtitle: "Courses belong to one learning library.",
        searchLabel: "Search courses",
        searchPlaceholder: "Search courses",
        sortOptions: [
          { value: "recent", label: "Recent" },
          { value: "title", label: "Title" },
          { value: "progress", label: "Progress" },
          { value: "programs", label: "Programs" },
        ],
      };
    case "course":
      return {
        title: "Programs",
        subtitle: "Programs belong to one course.",
        searchLabel: "Search programs",
        searchPlaceholder: "Search programs",
        sortOptions: [
          { value: "recent", label: "Recent" },
          { value: "title", label: "Title" },
          { value: "progress", label: "Progress" },
          { value: "modules", label: "Modules" },
        ],
      };
    case "program":
      return {
        title: "Modules",
        subtitle: "Modules organize related lessons.",
        searchLabel: "Search modules",
        searchPlaceholder: "Search modules",
        sortOptions: [
          { value: "recent", label: "Recent" },
          { value: "title", label: "Title" },
          { value: "progress", label: "Progress" },
          { value: "lessons", label: "Lessons" },
        ],
      };
    case "module":
      return {
        title: "Lessons",
        subtitle: "Lessons hold AI content, quizzes, labs, and study progress.",
        searchLabel: "Search lessons",
        searchPlaceholder: "Search lessons",
        sortOptions: [
          { value: "recent", label: "Recent" },
          { value: "title", label: "Title" },
          { value: "difficulty", label: "Difficulty" },
          { value: "status", label: "Status" },
        ],
      };
    default:
      return {
        title: "Learning Libraries",
        subtitle: "Organize your courses, explore lessons, and track your study progress.",
        searchLabel: "Search all learning",
        searchPlaceholder: "Search all learning",
        sortOptions: [
          { value: "recent", label: "Recent" },
          { value: "title", label: "Title" },
          { value: "courses", label: "Courses" },
        ],
      };
  }
}

function Field({ label, children }) {
  return (
    <label className="learning-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Breadcrumbs({ items, onNavigate }) {
  return (
    <div className="learning-breadcrumbs">
      {items.map((item, index) => (
        <React.Fragment key={`${item.type}-${item.id || "home"}`}>
          {index > 0 ? <span className="learning-breadcrumb-separator">/</span> : null}
          {item.isCurrent ? (
            <span className="learning-breadcrumb-current">{item.label}</span>
          ) : (
            <button type="button" className="action-link" onClick={() => onNavigate(item.type, item.id)}>
              {item.label}
            </button>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function SummaryCard({ item, pageType, onOpen, onEdit, onDelete, onGenerate, onOpenLesson, generation }) {
  return (
    <article className="learning-summary-card">
      <div className="learning-summary-card-top">
        <div>
          <span className="eyebrow">{pageType === "module" ? (item.itemType === "lab" ? "Lab" : "Lesson") : pageType === "program" ? "Module" : pageType === "course" ? "Program" : pageType === "library" ? "Course" : "Library"}</span>
          <h3>{onOpen ? <button type="button" className="learning-title-link" onClick={onOpen}>{item.title}</button> : item.title}</h3>
        </div>
        <div className="learning-summary-actions">
          {onOpen ? <button type="button" className="action-button" onClick={onOpen}>Open</button> : null}
          <details className="learning-card-tools"><summary aria-label={`Actions for ${item.title}`}>Actions</summary><div><button type="button" className="action-link" onClick={onEdit}>Edit</button><button type="button" className="action-link danger" onClick={onDelete}>Delete</button></div></details>
        </div>
      </div>
      {item.description || item.summary ? <p>{item.description || item.summary}</p> : null}
      <div className="learning-summary-meta">
        {pageType === "home" ? <span>{item.courseCount || 0} course{item.courseCount === 1 ? "" : "s"}</span> : null}
        {pageType === "library" ? <span>{item.programCount || 0} program{item.programCount === 1 ? "" : "s"}</span> : null}
        {pageType === "course" ? <span>{item.moduleCount || 0} module{item.moduleCount === 1 ? "" : "s"}</span> : null}
        {pageType === "program" ? <span>{item.lessonCount || 0} lesson{item.lessonCount === 1 ? "" : "s"}</span> : null}
        {pageType !== "home" ? <span>{formatProgress(item.progressPercent)}</span> : null}
        {item.difficulty ? <span>{item.difficulty}</span> : null}
        {item.certificationLevel ? <span>{item.certificationLevel}</span> : null}
        {item.status ? <span>{labelizeStatus(item.status)}</span> : null}
        {item.estimatedDurationMinutes ? <span>{formatMinutes(item.estimatedDurationMinutes)}</span> : null}
        {item.estimatedMinutes ? <span>{formatMinutes(item.estimatedMinutes)}</span> : null}
        <span>Updated {formatDateTime(item.lastUpdated || item.updatedAt || item.lastStudied || item.lastActivity || item.lastActivityAt)}</span>
      </div>
      {pageType !== "home" && Number.isFinite(Number(item.progressPercent)) ? <progress className="learning-progress" max="100" value={Math.min(100, Math.max(0, Number(item.progressPercent)))} aria-label={`Progress for ${item.title}`} /> : null}
      {pageType === "module" ? (
        <div className="learning-lesson-actions">
          <button type="button" className="action-button action-button-secondary" onClick={onGenerate} disabled={generation?.pending} aria-busy={Boolean(generation?.pending)}>
            {generation?.pending ? "Generating…" : item.hasLessonContent ? `Regenerate AI ${item.itemType === "lab" ? "lab" : "lesson"}` : `Generate AI ${item.itemType === "lab" ? "lab" : "lesson"}`}
          </button>
          <button
            type="button"
            className="action-button"
            onClick={onOpenLesson}
            disabled={!item.hasLessonContent}
          >
            {item.itemType === "lab" ? "Open lab" : "Open lesson"}
          </button>
        </div>
      ) : null}
      {pageType === "module" ? <div className="learning-generation-feedback">
        {generation?.pending ? <p role="status">Generating your lesson. This may take a little while.</p> : null}
        {generation?.success ? <p role="status">{generation.success}</p> : null}
        {generation?.error ? <p role="alert" className="generation-error">{generation.error} {item.hasLessonContent ? "Your previously saved lesson is still available." : "You can retry generation."}</p> : null}
        {item.generatedAt ? <small>Generated {formatDateTime(item.generatedAt)} · Version {item.generationVersion || 1}</small> : null}
      </div> : null}
    </article>
  );
}

export default function LearningView({
  pageType = "home",
  entityId = "",
  onNavigate,
  onOpenLesson,
}) {
  const pageMeta = useMemo(() => buildPageMeta(pageType), [pageType]);
  const [noteFolderCount,setNoteFolderCount]=useState(0);
  useEffect(()=>{setNoteFolderCount(0);},[entityId,pageType]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generationById, setGenerationById] = useState({});
  const generationRequests = useRef(new Set());
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortValue, setSortValue] = useState(pageType === "home" ? "relevance" : "recent");
  const [searchPage,setSearchPage]=useState(1);
  const [searchData,setSearchData]=useState({results:[],total:0,page:1});
  const globalSearch=pageType === 'home' && !!searchQuery.trim();
  useEffect(()=>{setSearchPage(1);},[searchQuery,sortValue]);
  useEffect(()=>{
    if(!globalSearch)return;
    let cancelled=false;
    setLoading(true);setErrorMessage('');setSearchData({results:[],total:0,page:1});
    const timer=setTimeout(async()=>{
      try{const res=await api.get(`/api/learning/search?${new URLSearchParams({q:searchQuery.trim(),sort:sortValue,page:String(searchPage)})}`);
        if(!cancelled)setSearchData(res.data);
      }catch(error){if(!cancelled)setErrorMessage(error.response?.data?.error || 'Could not search learning content. Please try again.');}
      finally{if(!cancelled)setLoading(false);}
    },300);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[globalSearch,searchQuery,sortValue,searchPage]);
  const [bootstrap, setBootstrap] = useState({ domains: [], topics: [] });
  const [currentEntity, setCurrentEntity] = useState(null);
  const [context, setContext] = useState(null);
  const [items, setItems] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [libraryForm, setLibraryForm] = useState(EMPTY_LIBRARY_FORM);
  const [courseForm, setCourseForm] = useState(EMPTY_COURSE_FORM);
  const [programForm, setProgramForm] = useState(EMPTY_PROGRAM_FORM);
  const [moduleForm, setModuleForm] = useState(EMPTY_MODULE_FORM);
  const [lessonForm, setLessonForm] = useState(EMPTY_LESSON_FORM);

  const topicsByDomain = useMemo(() => (
    (Array.isArray(bootstrap.topics) ? bootstrap.topics : []).reduce((accumulator, topic) => {
      const key = topic.domainId || "";
      if (!accumulator[key]) accumulator[key] = [];
      accumulator[key].push(topic);
      return accumulator;
    }, {})
  ), [bootstrap.topics]);

  const programById = useMemo(() => (
    (Array.isArray(programOptions) ? programOptions : []).reduce((accumulator, program) => {
      accumulator[program.id] = program;
      return accumulator;
    }, {})
  ), [programOptions]);

  const selectedProgram = useMemo(() => {
    if (pageType !== "program") return null;
    return programById[moduleForm.programId] || currentEntity || null;
  }, [currentEntity, moduleForm.programId, pageType, programById]);

  const moduleNameOptions = useMemo(() => {
    if (pageType !== "program") return [];
    const trackId = inferTrackIdFromProgram(selectedProgram, bootstrap.tracks);
    const domainOptions = (Array.isArray(bootstrap.domains) ? bootstrap.domains : [])
      .filter((domain) => domain.trackId === trackId)
      .map((domain) => domain.title)
      .filter(Boolean);
    const existingTitle = String(moduleForm.title || "").trim();
    return [...new Set([...domainOptions, existingTitle].filter(Boolean))];
  }, [bootstrap.domains, bootstrap.tracks, moduleForm.title, pageType, selectedProgram]);

  useEffect(() => {
    let cancelled = false;

    async function loadView() {
      if(pageType === "home" && searchQuery.trim())return;
      setLoading(true);
      setErrorMessage("");
      try {
        const params = new URLSearchParams();
        if (searchQuery.trim()) params.set("q", searchQuery.trim());
        if (sortValue) params.set("sort", sortValue);
        const suffix = params.toString() ? `?${params.toString()}` : "";

        if (pageType === "home") {
          const res = await api.get(`/api/learning/libraries${suffix}`);
          if (cancelled) return;
          setCurrentEntity(null);
          setContext(null);
          setItems(Array.isArray(res.data?.libraries) ? res.data.libraries : []);
          setProgramOptions([]);
        } else if (pageType === "library" && entityId) {
          const [libraryRes, coursesRes] = await Promise.all([
            api.get(`/api/learning/libraries/${entityId}`),
            api.get(`/api/learning/libraries/${entityId}/courses${suffix}`),
          ]);
          if (cancelled) return;
          setCurrentEntity(libraryRes.data?.library || null);
          setContext(null);
          setItems(Array.isArray(coursesRes.data?.courses) ? coursesRes.data.courses : []);
          setProgramOptions([]);
        } else if (pageType === "course" && entityId) {
          const [courseRes, programsRes] = await Promise.all([
            api.get(`/api/learning/courses/${entityId}`),
            api.get(`/api/learning/courses/${entityId}/programs${suffix}`),
          ]);
          if (cancelled) return;
          setCurrentEntity(courseRes.data?.course || null);
          setContext(null);
          setItems(Array.isArray(programsRes.data?.programs) ? programsRes.data.programs : []);
          setProgramOptions([]);
        } else if (pageType === "program" && entityId) {
          const [programRes, modulesRes, bootstrapRes] = await Promise.all([
            api.get(`/api/learning/programs/${entityId}`),
            api.get(`/api/learning/programs/${entityId}/modules${suffix}`),
            api.get("/api/learning/bootstrap"),
          ]);
          const nextProgram = programRes.data?.program || null;
          const siblingProgramsRes = nextProgram?.courseId
            ? await api.get(`/api/learning/courses/${nextProgram.courseId}/programs`)
            : null;
          if (cancelled) return;
          setCurrentEntity(nextProgram);
          setContext(null);
          setItems(Array.isArray(modulesRes.data?.modules) ? modulesRes.data.modules : []);
          setProgramOptions(Array.isArray(siblingProgramsRes?.data?.programs) ? siblingProgramsRes.data.programs : []);
          setBootstrap({
            tracks: Array.isArray(bootstrapRes.data?.tracks) ? bootstrapRes.data.tracks : [],
            domains: Array.isArray(bootstrapRes.data?.domains) ? bootstrapRes.data.domains : [],
            topics: Array.isArray(bootstrapRes.data?.topics) ? bootstrapRes.data.topics : [],
          });
        } else if (pageType === "module" && entityId) {
          const [moduleRes, lessonsRes, bootstrapRes] = await Promise.all([
            api.get(`/api/learning/modules/${entityId}`),
            api.get(`/api/learning/modules/${entityId}/lessons${suffix}`),
            api.get("/api/learning/bootstrap"),
          ]);
          if (cancelled) return;
          setCurrentEntity(moduleRes.data?.module || null);
          setContext(moduleRes.data?.context || lessonsRes.data?.context || null);
          setItems(Array.isArray(lessonsRes.data?.lessons) ? lessonsRes.data.lessons : []);
          setBootstrap({
            tracks: Array.isArray(bootstrapRes.data?.tracks) ? bootstrapRes.data.tracks : [],
            domains: Array.isArray(bootstrapRes.data?.domains) ? bootstrapRes.data.domains : [],
            topics: Array.isArray(bootstrapRes.data?.topics) ? bootstrapRes.data.topics : [],
          });
          setProgramOptions([]);
        } else {
          onNavigate?.("home", "");
          return;
        }
      } catch (error) {
        if (cancelled) return;
        setErrorMessage(error.response?.data?.error || "Could not load the learning page.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadView();
    return () => {
      cancelled = true;
    };
  }, [entityId, onNavigate, pageType, searchQuery, sortValue]);

  useEffect(() => {
    setEditingId("");
    setShowCreateForm(false);
    setLibraryForm(EMPTY_LIBRARY_FORM);
    setCourseForm(EMPTY_COURSE_FORM);
    setProgramForm(EMPTY_PROGRAM_FORM);
    setModuleForm({ ...EMPTY_MODULE_FORM, programId: pageType === "program" ? entityId : "" });
    setLessonForm(EMPTY_LESSON_FORM);
    setMessage("");
  }, [pageType, entityId]);

  useEffect(() => {
    if (pageType !== "program") return;
    if (!moduleNameOptions.length) return;

    setModuleForm((current) => {
      if (current.title && moduleNameOptions.includes(current.title)) {
        return current;
      }
      if (current.title && !moduleNameOptions.includes(current.title)) {
        return current;
      }
      return {
        ...current,
        title: moduleNameOptions[0],
      };
    });
  }, [moduleNameOptions, pageType]);

  const breadcrumbs = useMemo(() => {
    const trail = [{ type: "home", id: "", label: "Learning", isCurrent: pageType === "home" }];
    if (pageType === "library" && currentEntity) {
      trail.push({ type: "library", id: currentEntity.id, label: currentEntity.title, isCurrent: true });
    }
    if (pageType === "course" && currentEntity) {
      trail.push({ type: "home", id: "", label: currentEntity.libraryTitle || "Library", isCurrent: false });
      trail.push({ type: "course", id: currentEntity.id, label: currentEntity.title, isCurrent: true });
    }
    if (pageType === "program" && currentEntity) {
      trail.push({ type: "course", id: currentEntity.courseId, label: currentEntity.courseTitle || "Course", isCurrent: false });
      trail.push({ type: "program", id: currentEntity.id, label: currentEntity.title, isCurrent: true });
    }
    if (pageType === "module" && currentEntity) {
      if (context?.courseId) {
        trail.push({ type: "course", id: context.courseId, label: context.courseTitle || "Course", isCurrent: false });
      }
      if (context?.programId) {
        trail.push({ type: "program", id: context.programId, label: context.programTitle || "Program", isCurrent: false });
      }
      trail.push({ type: "module", id: currentEntity.id, label: currentEntity.title, isCurrent: true });
    }
    return trail;
  }, [context, currentEntity, pageType]);

  const createButtonLabel = pageType === "home"
    ? "Create Learning Library"
    : pageType === "library"
      ? "Create Course"
      : pageType === "course"
        ? "Create Program"
        : pageType === "program"
          ? "Create Module"
          : "Create Lesson";

  const beginEdit = (item) => {
    setEditingId(item.id);
    setShowCreateForm(true);
    setMessage("");
    if (pageType === "home") {
      setLibraryForm({
        title: item.title || "",
        description: item.description || "",
      });
    } else if (pageType === "library") {
      setCourseForm({
        title: item.title || "",
        description: item.description || "",
        difficulty: item.difficulty || "intermediate",
      });
    } else if (pageType === "course") {
      setProgramForm({
        title: item.title || "",
        certificationLevel: item.certificationLevel || "",
        description: item.description || "",
        status: item.status || "not_started",
        progressPercent: item.progressPercent || 0,
        estimatedDurationMinutes: item.estimatedDurationMinutes || 25,
      });
    } else if (pageType === "program") {
      setModuleForm({
        programId: item.programId || entityId || "",
        title: item.title || "",
        description: item.description || "",
        status: item.status || "not_started",
        progressPercent: item.progressPercent || 0,
        estimatedDurationMinutes: item.estimatedDurationMinutes || 25,
      });
    } else {
      setLessonForm({
        title: item.title || "",
        summary: item.summary || "",
        content: item.content || "",
        difficulty: item.difficulty || "intermediate",
        itemType: item.itemType || "lesson",
        estimatedMinutes: item.estimatedMinutes || 25,
        domainId: item.domainId || "",
        topicId: item.topicId || "",
      });
    }
  };

  const resetForm = () => {
    setEditingId("");
    setShowCreateForm(false);
    setLibraryForm(EMPTY_LIBRARY_FORM);
    setCourseForm(EMPTY_COURSE_FORM);
    setProgramForm(EMPTY_PROGRAM_FORM);
    setModuleForm({ ...EMPTY_MODULE_FORM, programId: pageType === "program" ? entityId : "" });
    setLessonForm(EMPTY_LESSON_FORM);
  };

  const refreshAfterSave = async () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("q", searchQuery.trim());
    if (sortValue) params.set("sort", sortValue);
    const suffix = params.toString() ? `?${params.toString()}` : "";

    if (pageType === "home") {
      const res = await api.get(`/api/learning/libraries${suffix}`);
      setItems(Array.isArray(res.data?.libraries) ? res.data.libraries : []);
      return;
    }
    if (pageType === "library" && entityId) {
      const res = await api.get(`/api/learning/libraries/${entityId}/courses${suffix}`);
      setItems(Array.isArray(res.data?.courses) ? res.data.courses : []);
      return;
    }
    if (pageType === "course" && entityId) {
      const res = await api.get(`/api/learning/courses/${entityId}/programs${suffix}`);
      setItems(Array.isArray(res.data?.programs) ? res.data.programs : []);
      return;
    }
    if (pageType === "program" && entityId) {
      const res = await api.get(`/api/learning/programs/${entityId}/modules${suffix}`);
      setItems(Array.isArray(res.data?.modules) ? res.data.modules : []);
      return;
    }
    if (pageType === "module" && entityId) {
      const res = await api.get(`/api/learning/modules/${entityId}/lessons${suffix}`);
      setItems(Array.isArray(res.data?.lessons) ? res.data.lessons : []);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrorMessage("");
    setMessage("");
    try {
      if (pageType === "home") {
        const payload = { ...libraryForm };
        if (editingId) {
          await api.put(`/api/learning/libraries/${editingId}`, payload);
          setMessage("Learning library updated.");
        } else {
          await api.post("/api/learning/libraries", payload);
          setMessage("Learning library created.");
        }
      } else if (pageType === "library") {
        const payload = { ...courseForm };
        if (editingId) {
          await api.put(`/api/learning/courses/${editingId}`, payload);
          setMessage("Course updated.");
        } else {
          await api.post(`/api/learning/libraries/${entityId}/courses`, payload);
          setMessage("Course created.");
        }
      } else if (pageType === "course") {
        const payload = { ...programForm };
        if (editingId) {
          await api.put(`/api/learning/programs/${editingId}`, payload);
          setMessage("Program updated.");
        } else {
          await api.post(`/api/learning/courses/${entityId}/programs`, payload);
          setMessage("Program created.");
        }
      } else if (pageType === "program") {
        const payload = { ...moduleForm };
        const targetProgramId = payload.programId || entityId;
        if (editingId) {
          await api.put(`/api/learning/modules/${editingId}`, payload);
          setMessage("Module updated.");
        } else {
          await api.post(`/api/learning/programs/${targetProgramId}/modules`, payload);
          setMessage("Module created.");
        }
      } else if (pageType === "module") {
        const payload = { ...lessonForm, trackId: lessonTrackId };
        if (editingId) {
          await api.put(`/api/learning/lessons/${editingId}`, payload);
          setMessage("Lesson updated.");
        } else {
          await api.post(`/api/learning/modules/${entityId}/lessons`, payload);
          setMessage("Lesson created.");
        }
      }

      resetForm();
      await refreshAfterSave();
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Could not save the learning record.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    setErrorMessage("");
    setMessage("");
    try {
      if (pageType === "home") {
        await api.delete(`/api/learning/libraries/${item.id}`);
      } else if (pageType === "library") {
        await api.delete(`/api/learning/courses/${item.id}`);
      } else if (pageType === "course") {
        await api.delete(`/api/learning/programs/${item.id}`);
      } else if (pageType === "program") {
        await api.delete(`/api/learning/modules/${item.id}`);
      } else {
        await api.delete(`/api/learning/lessons/${item.id}`);
      }
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      if (editingId === item.id) {
        resetForm();
      }
      setMessage("Deleted.");
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Could not delete this record.");
    }
  };

  const handleGenerateLesson = async (lesson) => {
    if (generationRequests.current.has(lesson.id)) return;
    generationRequests.current.add(lesson.id);
    setGenerationById(current => ({ ...current, [lesson.id]: { pending: true } }));
    setErrorMessage("");
    setMessage("");
    try {
      const res = await api.post(`/api/learning/study-items/${lesson.id}/generate`, {
        force: Boolean(lesson.hasLessonContent),
      });
      const nextLesson = res.data?.item;
      if (nextLesson) {
        setItems((current) => current.map((entry) => (entry.id === lesson.id ? nextLesson : entry)));
      }
      const success = res.data?.cached ? "Existing lesson content reused." : `AI ${lesson.itemType === "lab" ? "lab" : "lesson"} generated. Open ${lesson.itemType === "lab" ? "lab" : "lesson"} to view it.`;
      setGenerationById(current => ({ ...current, [lesson.id]: { success } }));
    } catch (error) {
      setGenerationById(current => ({ ...current, [lesson.id]: { error: error.response?.data?.error || "Could not generate AI lesson. Please try again." } }));
    } finally {
      generationRequests.current.delete(lesson.id);
    }
  };

  const automationContext = /automation|devops|pyez|netconf/i.test(`${currentEntity?.title || ""} ${context?.programTitle || ""} ${lessonForm.title || ""}`);
  const lessonTrackId = automationContext ? "junos-automation" : inferTrackIdFromProgram({title: context?.programTitle, certificationLevel: context?.programTitle}, bootstrap.tracks);
  const visibleDomains = useMemo(() => bootstrap.domains.filter(domain => domain.trackId === lessonTrackId || (editingId && domain.id === lessonForm.domainId)), [bootstrap.domains, lessonTrackId, editingId, lessonForm.domainId]);
  const visibleTopics = topicsByDomain[lessonForm.domainId] || [];
  useEffect(() => {
    if (editingId) return;
    setLessonForm(current => {
      if (!current.domainId || visibleDomains.some(domain => domain.id === current.domainId)) return current;
      return {...current, domainId: "", topicId: ""};
    });
  }, [visibleDomains, editingId]);

  const notesOnly = pageType === "program" && noteFolderCount>0 && currentEntity?.moduleCount===0;
  return (
    <section className="learning-workspace">
      <div className="learning-workspace-top">
        <div>
          <span className="eyebrow">Learning</span>
          <h2>{notesOnly ? "Notes" : pageMeta.title}</h2>
          <p className="learning-workspace-copy">{notesOnly ? "Notes stored directly in this program." : pageMeta.subtitle}</p>
          {pageType !== "home" ? <Breadcrumbs items={breadcrumbs} onNavigate={onNavigate} /> : null}
        </div>
        <button
          type="button"
          className="action-button"
          hidden={notesOnly}
          onClick={() => {
            setShowCreateForm((current) => !current);
            if (showCreateForm) {
              resetForm();
            }
          }}
        >
          {createButtonLabel}
        </button>
      </div>

      {pageType === "program" && (currentEntity?.certificationLevel === 'JNCIP-SP' || currentEntity?.title === 'JNCIP-SP') ? <ExamCoveragePanel key={entityId+'coverage'} programId={entityId} /> : null}
      {pageType === "program" && currentEntity && !notesOnly ? <AiModulesPanel key={entityId} programId={entityId} onSaved={refreshAfterSave} /> : null}
      <section className="learning-toolbar-panel" hidden={notesOnly}>
        <Field label={pageMeta.searchLabel}>
          <input
            type="search"
            className="form-control"
            value={searchQuery}
            onChange={(event) => { setSearchQuery(event.target.value); if(pageType === "home" && !searchQuery.trim() && event.target.value.trim())setSortValue("relevance"); }}
            placeholder={pageMeta.searchPlaceholder}
          />
        </Field>
        <Field label="Sort">
          <select className="form-control" value={sortValue} onChange={(event) => setSortValue(event.target.value)}>
            {(globalSearch ? [{value:'relevance',label:'Relevance'},{value:'recent',label:'Recent'},{value:'title',label:'Title'}] : pageType==='home' ? [{value:'relevance',label:'Recent'},...pageMeta.sortOptions.filter(o=>o.value!=='recent')] : pageMeta.sortOptions).map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
      </section>

      {errorMessage ? <div className="feedback-panel error">{errorMessage}</div> : null}
      {message ? <div className="feedback-panel">{message}</div> : null}

      {showCreateForm ? (
        <section className="learning-editor-panel">
          <div className="section-heading section-heading-compact">
            <div>
              <span className="eyebrow">{editingId ? "Edit" : "Create"}</span>
              <h2>{editingId ? "Update record" : createButtonLabel}</h2>
            </div>
          </div>
          <form className="learning-entity-form" onSubmit={handleSubmit}>
            {pageType === "home" ? (
              <>
                <Field label="Library name">
                  <input className="form-control" value={libraryForm.title} onChange={(event) => setLibraryForm((current) => ({ ...current, title: event.target.value }))} />
                </Field>
                <Field label="Description">
                  <textarea className="form-control learning-textarea" value={libraryForm.description} onChange={(event) => setLibraryForm((current) => ({ ...current, description: event.target.value }))} />
                </Field>
              </>
            ) : null}

            {pageType === "library" ? (
              <>
                <Field label="Course name">
                  <input className="form-control" value={courseForm.title} onChange={(event) => setCourseForm((current) => ({ ...current, title: event.target.value }))} />
                </Field>
                <Field label="Difficulty">
                  <select className="form-control" value={courseForm.difficulty} onChange={(event) => setCourseForm((current) => ({ ...current, difficulty: event.target.value }))}>
                    <option value="foundation">Foundation</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="exam">Exam</option>
                    <option value="stretch">Stretch</option>
                  </select>
                </Field>
                <Field label="Description">
                  <textarea className="form-control learning-textarea" value={courseForm.description} onChange={(event) => setCourseForm((current) => ({ ...current, description: event.target.value }))} />
                </Field>
              </>
            ) : null}

            {pageType === "course" ? (
              <>
                <Field label="Program name">
                  <input className="form-control" value={programForm.title} onChange={(event) => setProgramForm((current) => ({ ...current, title: event.target.value }))} />
                </Field>
                <Field label="Certification level">
                  <input className="form-control" value={programForm.certificationLevel} onChange={(event) => setProgramForm((current) => ({ ...current, certificationLevel: event.target.value }))} />
                </Field>
                <Field label="Status">
                  <select className="form-control" value={programForm.status} onChange={(event) => setProgramForm((current) => ({ ...current, status: event.target.value }))}>
                    <option value="not_started">Not started</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </Field>
                <Field label="Estimated duration (minutes)">
                  <input className="form-control" type="number" min="10" max="240" value={programForm.estimatedDurationMinutes} onChange={(event) => setProgramForm((current) => ({ ...current, estimatedDurationMinutes: event.target.value }))} />
                </Field>
                <Field label="Progress percent">
                  <input className="form-control" type="number" min="0" max="100" value={programForm.progressPercent} onChange={(event) => setProgramForm((current) => ({ ...current, progressPercent: event.target.value }))} />
                </Field>
                <Field label="Description">
                  <textarea className="form-control learning-textarea" value={programForm.description} onChange={(event) => setProgramForm((current) => ({ ...current, description: event.target.value }))} />
                </Field>
              </>
            ) : null}

            {pageType === "program" ? (
              <>
                <Field label="Program">
                  <select
                    className="form-control"
                    value={moduleForm.programId}
                    onChange={(event) => setModuleForm((current) => ({ ...current, programId: event.target.value }))}
                  >
                    <option value="">Select program</option>
                    {programOptions.map((program) => (
                      <option key={program.id} value={program.id}>{program.title}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Module name">
                  <select
                    className="form-control"
                    value={moduleForm.title}
                    onChange={(event) => setModuleForm((current) => ({ ...current, title: event.target.value }))}
                  >
                    <option value="">Select module</option>
                    {moduleNameOptions.map((moduleTitle) => (
                      <option key={moduleTitle} value={moduleTitle}>{moduleTitle}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Status">
                  <select className="form-control" value={moduleForm.status} onChange={(event) => setModuleForm((current) => ({ ...current, status: event.target.value }))}>
                    <option value="not_started">Not started</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </Field>
                <Field label="Estimated duration (minutes)">
                  <input className="form-control" type="number" min="10" max="240" value={moduleForm.estimatedDurationMinutes} onChange={(event) => setModuleForm((current) => ({ ...current, estimatedDurationMinutes: event.target.value }))} />
                </Field>
                <Field label="Progress percent">
                  <input className="form-control" type="number" min="0" max="100" value={moduleForm.progressPercent} onChange={(event) => setModuleForm((current) => ({ ...current, progressPercent: event.target.value }))} />
                </Field>
                <Field label="Description">
                  <textarea className="form-control learning-textarea" value={moduleForm.description} onChange={(event) => setModuleForm((current) => ({ ...current, description: event.target.value }))} />
                </Field>
              </>
            ) : null}

            {pageType === "module" ? (
              <>
                <Field label="Lesson title">
                  <input className="form-control" required value={lessonForm.title} onChange={(event) => setLessonForm((current) => ({ ...current, title: event.target.value }))} />
                </Field>
                <Field label="Lesson type">
                  <select className="form-control" value={lessonForm.itemType} onChange={(event) => setLessonForm((current) => ({ ...current, itemType: event.target.value }))}>
                    <option value="lesson">Lesson</option>
                    <option value="lab">Lab</option>
                    <option value="quiz">Quiz</option>
                    <option value="flashcard_set">Flashcard set</option>
                    <option value="verification_exercise">Verification exercise</option>
                    <option value="troubleshooting_scenario">Troubleshooting scenario</option>
                  </select>
                </Field>
                <Field label="Difficulty">
                  <select className="form-control" value={lessonForm.difficulty} onChange={(event) => setLessonForm((current) => ({ ...current, difficulty: event.target.value }))}>
                    <option value="foundation">Foundation</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="exam">Exam</option>
                    <option value="stretch">Stretch</option>
                  </select>
                </Field>
                <Field label="Estimated duration (minutes)">
                  <input className="form-control" type="number" min="10" max="240" value={lessonForm.estimatedMinutes} onChange={(event) => setLessonForm((current) => ({ ...current, estimatedMinutes: event.target.value }))} />
                </Field>
                <Field label="Domain">
                  <select className="form-control" value={lessonForm.domainId} onChange={(event) => setLessonForm((current) => ({ ...current, domainId: event.target.value, topicId: "" }))}>
                    <option value="">Select domain</option>
                    {visibleDomains.map((domain) => (
                      <option key={domain.id} value={domain.id}>{domain.title}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Topic">
                  <select className="form-control" value={lessonForm.topicId} onChange={(event) => setLessonForm((current) => ({ ...current, topicId: event.target.value }))}>
                    <option value="">Select topic</option>
                    {visibleTopics.map((topic) => (
                      <option key={topic.id} value={topic.id}>{topic.title}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Description">
                  <textarea
                    className="form-control learning-textarea"
                    value={lessonForm.summary}
                    onChange={(event) => setLessonForm((current) => ({ ...current, summary: event.target.value }))}
                    placeholder="Add a short lesson description"
                  />
                </Field>
                <Field label="Source content (optional)">
                  <textarea
                    className="form-control learning-textarea learning-textarea-large"
                    value={lessonForm.content}
                    onChange={(event) => setLessonForm((current) => ({ ...current, content: event.target.value }))}
                    placeholder="Add source content, notes, commands, or lesson details. You can also create the lesson first and generate its content with AI."
                  />
                </Field>
              </>
            ) : null}

            <div className="dashboard-actions">
              <button type="submit" className="action-button" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update" : "Create"}
              </button>
              <button type="button" className="action-button action-button-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {pageType === "program" && currentEntity ? <LearningNoteFolders key={entityId+"folders"} programId={entityId} onFolderCountChange={setNoteFolderCount} parentTitle={currentEntity.courseTitle} onProgramBack={()=>onNavigate("course",currentEntity.courseId)}/> : null}
      {currentEntity && pageType !== "home" && !notesOnly ? (
        <section className="learning-context-panel">
          <span className="eyebrow">Current {pageType}</span>
          <h3>{currentEntity.title}</h3>
          {currentEntity.description ? <LinkedLearningDescription text={currentEntity.description} /> : null}
          {pageType === "course" ? <CourseStatusNote key={entityId+"status"} courseId={entityId}/> : null}
        </section>
      ) : null}

      {loading ? <div className="feedback-panel">Loading learning content...</div> : null}
      {globalSearch && !loading && !errorMessage ? <section aria-label="Learning search results">
        <p>{searchData.total} matches</p>
        {!searchData.total ? <div className="learning-empty-state"><strong>No matches for “{searchQuery.trim()}”</strong><button type="button" onClick={()=>setSearchQuery('')}>Clear search</button></div> : <>
          <div className="learning-summary-grid">{searchData.results.map(result=><article className="learning-summary-card" key={result.type+result.id}>
            <span className="eyebrow">{result.type}</span><h2>{result.title}</h2><p>{result.breadcrumb}</p><p>{result.excerpt}</p>
            <button className="btn btn-primary" type="button" onClick={()=>{
              if(['lesson','lab'].includes(result.type))onOpenLesson?.(result.id,'module',result.destination.moduleId);
              else onNavigate?.(result.type,result.id);
            }}>Open {result.type}</button>
          </article>)}</div>
          <nav aria-label="Search pages"><button type="button" disabled={searchData.page<=1} onClick={()=>setSearchPage(searchData.page-1)}>Previous</button> <span>Page {searchData.page} of {Math.ceil(searchData.total/25)}</span> <button type="button" disabled={searchData.page*25>=searchData.total} onClick={()=>setSearchPage(searchData.page+1)}>Next</button></nav>
          <button type="button" onClick={()=>setSearchQuery('')}>Clear search</button>
        </>}
      </section> : null}

      {!globalSearch && !loading && !items.length && !notesOnly ? (
        <div className="learning-empty-state">
          <strong>{pageType === "program" ? "No modules yet." : "No records yet."}</strong>
          <span>Use “{createButtonLabel}” to add the first one.</span>
        </div>
      ) : null}

      {!globalSearch && !loading && !!items.length ? (
        <div className="learning-summary-grid">
          {items.map((item) => (
            <SummaryCard
              key={item.id}
              item={item}
              pageType={pageType}
              onOpen={pageType === "home"
                ? () => onNavigate?.("library", item.id)
                : pageType === "library"
                  ? () => onNavigate?.("course", item.id)
                  : pageType === "course"
                    ? () => onNavigate?.("program", item.id)
                    : pageType === "program"
                      ? () => onNavigate?.("module", item.id)
                      : null}
              onEdit={() => beginEdit(item)}
              onDelete={() => handleDelete(item)}
              generation={generationById[item.id]}
              onGenerate={pageType === "module" ? () => handleGenerateLesson(item) : undefined}
              onOpenLesson={pageType === "module" ? () => onOpenLesson?.(item.id, "module", entityId) : undefined}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}






